import { ApprovingInvoiceReportsSearchQuery } from '@/context/services/approving_invoice_reports';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { DATE_FORMAT, format } from 'date-util';
import { useCallback, useMemo } from 'react';
import {
  FieldKey,
  FieldOrder,
  useConfigActiveSearchValuesOrder,
} from './useConfigActiveSearchValuesOrder';
import { useValueRetrieval } from './useValueRetrieval';

// Simplified types
type SearchValue = string | number | boolean | string[] | undefined;
export type SearchValueShow = {
  fieldLabel: string;
  valueShow: SearchValue | SearchValue[];
  concatChar?: string;
};

// Field value transformation mapping
type ValueTransformer = (
  value: string | string[]
) => string | (string | undefined)[];

// Utility functions
const hasValue = (
  field: FieldOrder,
  searchQuery: ApprovingInvoiceReportsSearchQuery
): boolean => {
  if (field.key === 'status') return true;
  if (typeof field.key === 'string') {
    return Boolean(searchQuery[field.key]);
  }
  if (field.canShowPartialValue) {
    return field.key.some((k) => Boolean(searchQuery[k]));
  }
  return field.key.every((k) => Boolean(searchQuery[k]));
};

// Type guards
const isStringValue = (value: unknown): value is string =>
  typeof value === 'string';
const isArrayValue = (value: unknown): value is string[] =>
  Array.isArray(value);

const useShowSearchValue = ({
  searchQuery = {},
  payeeOptions,
  deptOptions,
  reportDeptOptions,
  memberOptions,
  excludeFields,
}: {
  searchQuery?: ApprovingInvoiceReportsSearchQuery;
  payeeOptions: BaseOption[];
  deptOptions: BaseOption[];
  reportDeptOptions: BaseOption[];
  memberOptions: BaseOption[];
  excludeFields: FieldKey[];
}) => {
  const {
    getApPayeeValue,
    getDeptValues,
    getOfficeMemberValues,
    getInvoiceStatusValues,
    getInvoiceFilesStatusValues,
  } = useValueRetrieval();
  const { FIELD_ORDERS } = useConfigActiveSearchValuesOrder();
  const { t } = useTranslation();

  // Create field value transformation mapping
  const fieldValueTransformers = useMemo(() => {
    const transformers: Record<string, ValueTransformer> = {
      ap_payee_id: (value) =>
        getApPayeeValue(isStringValue(value) ? value : '', payeeOptions) || '',
      dept_ids: (value) =>
        getDeptValues(isArrayValue(value) ? value : [], deptOptions) || '',
      report_dept_ids: (value) =>
        getDeptValues(isArrayValue(value) ? value : [], reportDeptOptions) ||
        '',
      office_member_ids: (value) =>
        getOfficeMemberValues(
          isArrayValue(value) ? value : [],
          memberOptions
        ) || '',
      status: (value) =>
        getInvoiceStatusValues(isStringValue(value) ? value : '') || '',
      mf_file: (value) =>
        getInvoiceFilesStatusValues({
          value: isStringValue(value) ? value : '',
          isHideIfNoLabel: true,
        }) || '',
    };
    return transformers;
  }, [
    getApPayeeValue,
    getDeptValues,
    getOfficeMemberValues,
    getInvoiceStatusValues,
    getInvoiceFilesStatusValues,
    payeeOptions,
    deptOptions,
    reportDeptOptions,
    memberOptions,
  ]);

  // Filter active search fields
  const activeSearchFields = useMemo(() => {
    return FIELD_ORDERS.filter((field) => hasValue(field, searchQuery)).filter(
      (field) =>
        !excludeFields.some((excludeField) =>
          Array.isArray(field.key)
            ? field.key.includes(excludeField)
            : field.key === excludeField
        )
    );
  }, [searchQuery, FIELD_ORDERS, excludeFields]);

  // Transform field value
  const transformFieldValue = useCallback(
    (field: FieldOrder): SearchValueShow['valueShow'] => {
      // Handle single field keys
      if (typeof field.key === 'string') {
        let value = searchQuery[field.key];
        /** Default value for status field */
        if (field.key === 'status' && value === undefined) {
          value = '';
        }
        const transformer = fieldValueTransformers[field.key];

        if (transformer && (isStringValue(value) || isArrayValue(value))) {
          return transformer(value);
        }

        // Handle boolean values
        if (typeof value === 'boolean' && field.key === 'without_agent') {
          return value ? t('approving_invoice_reports.checked') : '';
        }
        return value;
      }
      /** Handle date field keys */
      if (field.isDateField) {
        return field.key
          .map((k) => searchQuery[k])
          .map((value) =>
            isStringValue(value) ? format(value, DATE_FORMAT) : value
          );
      }
      if (field.isAmountField) {
        return field.key
          .map((k) => searchQuery[k])
          .map((value) =>
            isStringValue(value) ? toCommaSeparated(value) : value
          );
      }
      // Handle array field keys (date ranges, amount ranges, etc.)
      const values = field.key.map((k) => searchQuery[k]);
      return values;
    },
    [searchQuery, fieldValueTransformers, t]
  );

  // Generate search values to show
  const searchValuesShow: SearchValueShow[] = useMemo(() => {
    return activeSearchFields.map((field) => ({
      fieldLabel: field.label,
      valueShow: transformFieldValue(field),
      concatChar: field.concatChar,
    }));
  }, [activeSearchFields, transformFieldValue]);
  return {
    searchValuesShow,
  };
};

export { useShowSearchValue };
